/**
 * Persona inference — TripSummary → suggested vibe-class signal +
 * qualitative tags for the post-trip Echo upload.
 *
 * Reference:
 *   docs/superpowers/plans/2026-04-27-vibe-network-mvp.md (Chunk 7 Task 7.7)
 *   docs/superpowers/research/2026-04-27-pal-social-network.md (Tracks A-E)
 *
 * Pure — no I/O. This file is the AUTHORITATIVE SPECIFICATION the Mochi
 * firmware C++ persona module must mirror exactly. Same input → same
 * output across both, so a Pal that grew up on phone-only ELM327 can
 * pair to a Mochi v1 device later without any persona drift (synthesis
 * resequencing — Mochi as premium-tier upgrade rather than launch SKU).
 */

import type { TripSummary } from './trip-session';

export interface DriveContext {
    /** unix-second when the trip started, used to bucket time-of-day. */
    started_at: number;
    /** Whether the trip began before sunrise (rough; refined in v2 with GPS). */
    is_night?: boolean;
}

export interface PersonaSignal {
    /** Dominant vibe-class signal this trip emits. */
    vibe_class: string;
    /** Confidence in [0,1] for the dominant class. */
    confidence: number;
    /** Controlled-vocab qualitative tags suitable for an Echo upload. */
    qualitative: string[];
    /** Controlled-vocab discomfort tags. */
    discomfort: string[];
    /** Bucketed time tag matching ALLOWED_TIME in webapp/src/echoes/schema. */
    time_bucket: 'early-morning' | 'morning' | 'midday' | 'afternoon' | 'evening' | 'night' | 'late-night';
    /** Bucketed duration tag matching ALLOWED_DURATION. */
    duration_bucket: '15-30min' | '30-60min' | '1-2h' | '2h+';
}

export function inferPersona(summary: TripSummary, ctx: DriveContext): PersonaSignal {
    const time_bucket = bucketTime(ctx.started_at);
    const duration_bucket = bucketDuration(summary.duration_s);

    // Dominant signal logic — order matters; first match wins.
    //   1. Aggressive (low smoothness + many brakes) → spirit_driver
    //   2. Calm (high smoothness, low jitter) → calm_explorer
    //   3. Night (between 22:00 and 05:00) → night_driver
    //   4. Default fallback → calm_explorer with low confidence
    // RPM/throttle sharpen the aggressive signal when speed data alone is ambiguous.
    // avg_rpm > 3000 or peak throttle > 75% suggests spirited engine use.
    const highRpm = summary.avg_rpm > 3000 || summary.max_rpm > 5000;
    const highThrottle = summary.max_throttle_pct > 75;
    const isAggressive = summary.smoothness < 0.45 || summary.hard_brakes >= 4
        || (highRpm && highThrottle);
    const isCalm = summary.smoothness >= 0.7 && summary.hard_brakes <= 1
        && !highRpm && !highThrottle;
    // Explicit `is_night === false` overrides time-bucket inference; that
    // lets callers (and tests) hard-pin daylight conditions when they
    // know better than the local clock (e.g. tz-confused test runners).
    const isNight = ctx.is_night === false
        ? false
        : ctx.is_night === true || time_bucket === 'night' || time_bucket === 'late-night';

    let vibe_class = 'calm_explorer';
    let confidence = 0.4;

    if (isAggressive) {
        vibe_class = 'spirit_driver';
        confidence = 0.7;
    } else if (isCalm && isNight) {
        vibe_class = 'night_driver';
        confidence = 0.75;
    } else if (isCalm) {
        vibe_class = 'calm_explorer';
        confidence = 0.7;
    } else if (isNight) {
        vibe_class = 'night_driver';
        confidence = 0.55;
    }

    return {
        vibe_class,
        confidence,
        qualitative: qualitativeForTrip(summary, vibe_class, isNight),
        discomfort: discomfortForTrip(summary),
        time_bucket,
        duration_bucket,
    };
}

function qualitativeForTrip(s: TripSummary, vibe_class: string, isNight: boolean): string[] {
    const tags: string[] = [];
    if (s.smoothness >= 0.7) tags.push('calm');
    if (s.smoothness >= 0.85 && s.hard_brakes === 0) tags.push('low_traffic');
    if (vibe_class === 'night_driver' || isNight) tags.push('atmospheric');
    if (s.distance_km >= 30 && s.smoothness >= 0.6) tags.push('scenic');
    return tags;
}

function discomfortForTrip(s: TripSummary): string[] {
    const tags: string[] = [];
    if (s.hard_brakes >= 3) tags.push('high_traffic');
    if (s.smoothness < 0.4) tags.push('high_traffic');     // dedup'd by Set below
    return Array.from(new Set(tags));
}

/**
 * Bucket a unix-second to the controlled time-bucket vocab.
 * Uses local time (Date constructor + getHours) — caller's locale.
 */
export function bucketTime(unixSec: number): PersonaSignal['time_bucket'] {
    const h = new Date(unixSec * 1000).getHours();
    if (h < 5)  return 'late-night';
    if (h < 7)  return 'early-morning';
    if (h < 11) return 'morning';
    if (h < 14) return 'midday';
    if (h < 17) return 'afternoon';
    if (h < 22) return 'evening';
    return 'night';
}

export function bucketDuration(seconds: number): PersonaSignal['duration_bucket'] {
    if (seconds < 30 * 60)        return '15-30min';
    if (seconds < 60 * 60)        return '30-60min';
    if (seconds < 2 * 60 * 60)    return '1-2h';
    return '2h+';
}
